import Parse from 'parse'
import marketplaceAPI from '@/services/marketplace'

export function initParse(serverURL, appId) {
  Parse.serverURL = serverURL
  Parse.initialize(appId)
}

/**
 * Map marketplace plugin to legacy format for compatibility
 */
function mapPluginToLegacyFormat(plugin) {
  // Extract developer info - API returns developer as object
  const developerId = plugin.developer?.id || plugin.developer_id
  
  return {
    id: plugin.id,
    name: plugin.name,
    slug: plugin.slug,
    url: plugin.entrypoint_url || plugin.url || '',
    kind: plugin.kind || 'Site', // Default to Site if not specified
    pluginKind: plugin.kind || 'Site', // For plugin type icons
    developer: developerId,
    developerData: {
      id: plugin.id,
      status: plugin.status === 'active' ? 'Ready for Sale' : plugin.status,
      installsCount: plugin.installs_count || 0,
      rating: plugin.rating || 0,
      installParams: plugin.install_params ? JSON.stringify(plugin.install_params) : '[]',
      sandboxPermissions: plugin.sandbox_permissions || plugin.sandboxPermissions || [],
      // Add other fields as needed
    },
    developerContent: {
      keyImage: plugin.key_image_url || plugin.thumbnail_url || plugin.image_url || plugin.screenshot_url || null,
      description: plugin.description || '',
      featured: plugin.featured || false,
      categories: plugin.categories || [],
      // Add other content fields as needed
    },
    // Include full plugin data for compatibility
    ...plugin,
  }
}

/**
 * Map marketplace installation to legacy format
 */
function mapInstallationToLegacyFormat(installation) {
  // Handle both old format (install_scope_type, install_scope_id) and new format (install_scope object)
  const installScopeType = installation.install_scope?.type || installation.install_scope_type
  const installScopeId = installation.install_scope?.id || installation.install_scope_id
  
  // Map plugin data to match expected format
  const plugin = installation.plugin || {}
  const version = installation.version || installation.plugin_version || {}
  
  return {
    id: installation.id,
    pluginId: installation.plugin_id || plugin.id,
    plugin: plugin,
    version: version,
    param: installation.config || {},
    installScopeType: installScopeType,
    installScopeId: installScopeId,
    status: installation.status,
    // Map to legacy format expected by components
    developerApp: {
      id: plugin.id,
      slug: plugin.slug,
      name: plugin.name,
      installParams: plugin.install_params ? JSON.stringify(plugin.install_params) : '[]',
      sandboxPermissions: plugin.sandbox_permissions || plugin.sandboxPermissions || [],
    },
    // Include full installation data
    ...installation,
  }
}

export const getReadyForSalePlugins = async (parseServerSiteId, currentUserId = null) => {
  try {
    // Fetch active plugins (available to everyone)
    const activePlugins = await marketplaceAPI.listPlugins({ status: 'active' })
    
    // Fetch draft plugins (only for developers to see their own)
    let draftPlugins = []
    if (currentUserId) {
      try {
        const allDraftPlugins = await marketplaceAPI.listPlugins({ status: 'draft' })
        // Filter to only show draft plugins owned by the current user
        // Check using developer.user_id (Forge user ID) for validation
        draftPlugins = allDraftPlugins.filter((plugin) => {
          const pluginUserId = plugin.developer?.user_id
          return pluginUserId && pluginUserId.toString() === currentUserId.toString()
        })
      } catch (error) {
        console.warn('Could not fetch draft plugins:', error)
      }
    }
    
    // Combine active and developer's draft plugins
    // Use a Set to avoid duplicates based on plugin ID
    const pluginMap = new Map()
    activePlugins.forEach((plugin) => {
      pluginMap.set(plugin.id, plugin)
    })
    draftPlugins.forEach((plugin) => {
      // Only add if not already in map (shouldn't happen, but safe)
      if (!pluginMap.has(plugin.id)) {
        pluginMap.set(plugin.id, plugin)
      }
    })
    
    const allPlugins = Array.from(pluginMap.values())
    const apps = allPlugins.map(mapPluginToLegacyFormat)
    
    // Filter by kind - if kind includes 'Site' or 'User', otherwise default to Site
    const siteApps = apps.filter((app) => {
      const kind = app.kind || 'Site'
      return kind.includes('Site') || (!kind.includes('User') && !kind.includes('site'))
    })
    const userApps = apps.filter((app) => {
      const kind = app.kind || ''
      return kind.includes('User') || kind.includes('user')
    })
    return { siteApps, userApps }
  } catch (error) {
    console.error('getReadyForSalePlugins error', error)
  }
  return { siteApps: [], userApps: [] }
}

export const getMyPluginsInDevelopment = async (
  parseServerSiteId,
  myDeveloperId
) => {
  try {
    // Get developer's plugins with draft status
    // Note: If API supported developer_id filter, we could use it here
    const allPlugins = await marketplaceAPI.listPlugins({ status: 'draft' })
    // Filter by developer if we have developer ID
    // Also filter out plugins that don't belong to this developer (workaround for API bug)
    const myPlugins = myDeveloperId
      ? allPlugins.filter((p) => {
          const pluginDeveloperId = p.developer_id || p.developer?.id
          return pluginDeveloperId === myDeveloperId
        })
      : allPlugins
    
    // Log warning if API returned plugins from other developers (when filtering by developer)
    if (myDeveloperId && allPlugins.length !== myPlugins.length) {
      console.warn(
        `API bug: listPlugins(status=draft) returned ${allPlugins.length} plugins, ` +
        `but only ${myPlugins.length} belong to developer ${myDeveloperId}. ` +
        `Filtered out ${allPlugins.length - myPlugins.length} incorrect plugins.`
      )
    }
    
    const apps = myPlugins.map(mapPluginToLegacyFormat)
    const siteApps = apps.filter((app) => app.kind.includes('Site'))
    const userApps = apps.filter((app) => app.kind.includes('User'))
    return [siteApps || [], userApps || []]
  } catch (error) {
    console.error('getMyPluginsInDevelopment error', error)
  }
  return [[], []]
}

export const getUserInstalledApps = async (parseServerSiteId, userId) => {
  try {
    const installations = await marketplaceAPI.listInstallations({
      scope_type: 'user',
      scope_id: userId,
      status: 'active',
    })
    return installations.map(mapInstallationToLegacyFormat)
  } catch (error) {
    console.error('getUserInstalledApps error', error)
  }
  return []
}

export const getSiteInstalledApps = async (parseServerSiteId, siteId) => {
  try {
    const installations = await marketplaceAPI.listInstallations({
      scope_type: 'site',
      scope_id: siteId,
      status: 'active',
    })
    return installations.map(mapInstallationToLegacyFormat)
  } catch (error) {
    console.error('getSiteInstalledApps error', error)
  }
  return []
}

export const installApp = async ({
  appId,
  userId,
  siteId,
  parseServerSiteId,
  pluginSlug,
  pluginVersionId,
  forgeUserId, // Optional: Forge user ID for draft plugin validation (preferred)
  developerId, // Optional: developer_id for draft plugin validation (legacy fallback)
}) => {
  try {
    // Determine scope type and ID
    const scopeType = siteId ? 'site' : 'user'
    const scopeId = siteId || userId

    // Get plugin details to check status
    let plugin = null
    let isDraftPlugin = false
    
    if (pluginSlug) {
      try {
        plugin = await marketplaceAPI.getPlugin(pluginSlug)
        isDraftPlugin = plugin.status === 'draft' || plugin.status === 'submitted' || plugin.status === 'approved'
      } catch (pluginError) {
        console.warn('Could not fetch plugin details:', pluginError)
        // Continue without plugin details - will try to install anyway
      }
    }

    // If no version ID provided, try to get the latest published version
    // For draft plugins, developers can install draft versions
    let versionId = pluginVersionId
    if (!versionId && pluginSlug) {
      try {
        const versions = await marketplaceAPI.getPluginVersions(pluginSlug)
        // For draft plugins, allow installing draft/submitted/approved versions if user is developer
        // Otherwise, only allow published versions
        const allowedStatuses = isDraftPlugin && forgeUserId
          ? ['published', 'approved', 'submitted', 'draft']
          : ['published', 'active']
        
        const availableVersion = versions.find(
          (v) => allowedStatuses.includes(v.status)
        )
        if (availableVersion) {
          versionId = availableVersion.id
        }
      } catch (versionError) {
        console.warn('Could not fetch plugin versions:', versionError)
      }
    }

    const installationParams = {
      plugin_id: appId, // Plugin ID
      plugin_version_id: versionId, // Use found version or provided one
      installation: {
        install_scope_type: scopeType,
        install_scope_id: scopeId.toString(),
        status: 'active',
        config: {},
      },
    }

    // Include user_id (Forge user ID) for draft plugin installations (preferred method)
    // This allows developers to install and test their own draft plugins
    if (isDraftPlugin && forgeUserId) {
      installationParams.user_id = forgeUserId
    } else if (isDraftPlugin && developerId) {
      // Legacy fallback - use developer_id if user_id not available
      installationParams.developer_id = developerId
    }

    const installation = await marketplaceAPI.createInstallation(installationParams)

    return installation.id
  } catch (error) {
    console.error('parse / installApp error', error)
    
    // Create a user-friendly error message
    let errorMessage = 'Failed to install the plugin.'
    
    if (error.message) {
      const lowerMessage = error.message.toLowerCase()
      if (lowerMessage.includes('no published version') || lowerMessage.includes('published version')) {
        errorMessage = 'This plugin is not yet available for installation. It needs to be published first.'
      } else if (lowerMessage.includes('not found') || lowerMessage.includes('does not exist')) {
        errorMessage = 'Plugin not found. It may have been removed or is no longer available.'
      } else if (lowerMessage.includes('already installed') || lowerMessage.includes('duplicate')) {
        errorMessage = 'This plugin is already installed.'
      } else if (error.status === 422) {
        errorMessage = error.message || 'Invalid installation request. Please try again.'
      } else if (error.status === 403 || error.status === 401) {
        errorMessage = 'You do not have permission to install this plugin.'
      } else if (error.status >= 500) {
        errorMessage = 'Server error. Please try again later.'
      } else {
        errorMessage = error.message || 'An unexpected error occurred during installation.'
      }
    }
    
    // Throw error with user-friendly message
    const userError = new Error(errorMessage)
    userError.originalError = error
    userError.status = error.status
    throw userError
  }
}

export const uninstallApp = async ({
  instanceId,
  userId,
  siteId,
  parseServerSiteId, // Deprecated: no longer needed for marketplace API
}) => {
  try {
    await marketplaceAPI.deleteInstallation(instanceId)
    return instanceId
  } catch (error) {
    console.error('parse / uninstallApp error', error)
    throw error // Re-throw to allow error handling in components
  }
}

export const updateAppInstance = async (
  parseServerSiteId,
  instanceId,
  param
) => {
  try {
    const installation = await marketplaceAPI.updateInstallation(instanceId, {
      config: typeof param === 'string' ? JSON.parse(param) : param,
    })
    return installation
  } catch (error) {
    console.error('parse / updateAppInstance error', error)
  }
  return null
}

// params: { siteId, userId, appSlug }
export const getAppInstanceFromAppSlug = async (params) => {
  try {
    const { siteId, userId, appSlug } = params
    const scopeType = siteId ? 'site' : 'user'
    const scopeId = siteId || userId

    const installation = await marketplaceAPI.getInstallationByScope(
      scopeType,
      scopeId,
      appSlug
    )

    if (installation) {
      // Map to legacy format expected by components
      return {
        id: installation.id,
        developerApp: installation.plugin
          ? {
              id: installation.plugin.id,
              name: installation.plugin.name,
              slug: installation.plugin.slug,
              installParams: JSON.stringify(installation.plugin.install_params || {}),
            }
          : null,
        param: installation.config || {},
        ...installation,
      }
    }
  } catch (error) {
    console.error('parse / getAppInstanceFromAppSlug error', error)
  }
  return null
}

// Update developer app params are { appDataId, installParams, status, parseServerSiteId }
export const updateDeveloperAppData = async (params) => {
  try {
    const { appDataId, status, parseServerSiteId } = params
    // Note: This may need plugin slug instead of appDataId
    // You may need to fetch the plugin first or pass slug in params
    // For now, assuming we can update by some identifier
    // This might need adjustment based on actual API structure
    console.warn('updateDeveloperAppData: May need plugin slug instead of appDataId')
    // TODO: Implement based on actual API requirements
    return null
  } catch (error) {
    console.error('parse / updateDeveloperAppData error', error)
  }
  return null
}

export const findDeveloperByEmail = async (parseServerSiteId, email) => {
  try {
    const developer = await marketplaceAPI.findDeveloperByEmail(email)
    return developer
  } catch (error) {
    console.error('parse / findDeveloperByEmail error', error)
  }
  return null
}

// Third party Picker related, for mural for now and no `parseServerSiteId` related
export const authorize = async () => {
  try {
    const authorizeRequest = await Parse.Cloud.run('authorize')
    if (authorizeRequest.success === true && authorizeRequest.url) {
      return authorizeRequest.url
    }
  } catch (error) {
    console.error('parse / authorize error', error)
  }
  return null
}

export const getRolesList = async (parseServerSiteId) => {
  try {
    const res = await Parse.Cloud.run('getRolesList', { parseServerSiteId })
    if (res.status === 'success') {
      return res.rolesList
    }
  } catch (error) {
    console.error('parse / getRolesList error', error)
  }
}
